import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Fire, Trophy } from "@phosphor-icons/react";
import { formatWorkDate } from "@/lib/time";
import { meta } from "@/lib/attendance";
import { toast } from "sonner";

const BREAKDOWN = [
    ["total_worked_days", "Worked"],
    ["absent_days", "Absences"],
    ["no_schedule_days", "No Schedule"],
    ["time_off_days", "Time Off"],
    ["unclassified_days", "Unclassified"],
];

/** Admin-only: day-by-day attendance history + audit trail for one employee. */
export const AttendanceHistoryDialog = ({ technicianId, dateFrom, dateTo, open, onOpenChange }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !technicianId) return;
        let cancelled = false;
        setLoading(true);
        const p = new URLSearchParams();
        if (dateFrom) p.set("date_from", dateFrom);
        if (dateTo) p.set("date_to", dateTo);
        api.get(`/attendance/employee/${technicianId}?${p.toString()}`)
            .then(({ data: d }) => { if (!cancelled) setData(d); })
            .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, technicianId, dateFrom, dateTo]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="attendance-history-dialog">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: "Outfit" }}>
                        {data?.employee?.full_name || "Attendance history"}
                    </DialogTitle>
                    <DialogDescription>
                        {data ? `${formatWorkDate(data.date_from)} → ${formatWorkDate(data.date_to)}` : "Loading…"}
                        {" · Attendance only — payroll and Time Records are never changed here."}
                    </DialogDescription>
                </DialogHeader>

                {loading || !data ? (
                    <Skeleton className="h-64 rounded-md" />
                ) : (
                    <div className="space-y-5">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-lg border border-[#E2E8F0] p-4">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Current streak</div>
                                <div className="flex items-center gap-2 mt-1">
                                    <Fire size={22} weight="fill" className="text-[#E6002C]" />
                                    <span className="text-2xl font-bold text-[#003366]" data-testid="history-current-streak">
                                        {data.streaks.current_streak}
                                    </span>
                                    <span className="text-xs text-slate-500">days</span>
                                </div>
                            </div>
                            <div className="rounded-lg border border-[#E2E8F0] p-4">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Longest streak</div>
                                <div className="flex items-center gap-2 mt-1">
                                    <Trophy size={20} weight="fill" className="text-amber-500" />
                                    <span className="text-2xl font-bold text-[#003366]" data-testid="history-longest-streak">
                                        {data.streaks.longest_streak}
                                    </span>
                                    <span className="text-xs text-slate-500">days</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2" data-testid="history-breakdown">
                            {BREAKDOWN.map(([key, label]) => (
                                <Badge key={key} className="bg-slate-100 text-slate-700 border border-slate-200">
                                    {label}: {data.breakdown[key] ?? 0}
                                </Badge>
                            ))}
                        </div>

                        <div className="divide-y divide-slate-100 border border-[#E2E8F0] rounded-lg">
                            {data.history.map((d) => {
                                const m = meta(d.status);
                                return (
                                    <div key={d.work_date} className="px-4 py-2.5" data-testid={`history-row-${d.work_date}`}>
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-sm font-medium text-[#1F2A44] w-28 shrink-0">
                                                {formatWorkDate(d.work_date)}
                                            </span>
                                            <span className="flex-1 text-xs text-slate-500 truncate">
                                                {d.sites.join(", ")}
                                                {d.note ? ` · ${d.note}` : ""}
                                            </span>
                                            <Badge className={`${m.badge} border text-[11px]`}>{m.short}</Badge>
                                        </div>
                                        {(d.audit_trail || []).length > 0 && (
                                            <div className="mt-1.5 space-y-0.5">
                                                {d.audit_trail.map((a) => (
                                                    <div key={`${a.at}-${a.from}-${a.to}`} className="text-[11px] text-slate-400">
                                                        {meta(a.from).short} → {meta(a.to).short} · {a.admin_name} ·{" "}
                                                        {new Date(a.at).toLocaleString()}
                                                        {a.note ? ` · "${a.note}"` : ""}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
